/**
 * A titled group of sidebar rows that folds under its header, with one header
 * action shown on hover or focus. Spec beside it in sidebar-section.md.
 *
 * @module components/ui/sidebar-section
 */
import { ChevronRightIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { TIMELINE_CHEVRON_CLASS, TIMELINE_DISCLOSURE_PANEL_CLASS } from "../chat/timelineDisclosure";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "./collapsible";

export function SidebarSection({
  label,
  open,
  onOpenChange,
  action,
  children,
}: {
  readonly label: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Revealed while the header is hovered or focused, or its popup is open. */
  readonly action?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="flex flex-col" data-slot="sidebar-section">
      <div className="group/header flex h-7 items-center pe-[7px]" data-slot="sidebar-section-header">
        <CollapsibleTrigger className="group/section flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded-md ps-[11px] pe-2.5 text-left text-xs font-medium text-sidebar-foreground/50 outline-hidden ring-ring focus-visible:ring-2">
          <span className="min-w-0 truncate">{label}</span>
          <ChevronRightIcon
            aria-hidden
            strokeWidth={1.75}
            className={cn(
              TIMELINE_CHEVRON_CLASS,
              "opacity-0 group-hover/section:opacity-70",
              open && "rotate-90",
              !open && "opacity-70",
            )}
          />
        </CollapsibleTrigger>
        {action ? (
          <div
            data-slot="sidebar-section-action"
            className="ms-auto flex opacity-0 transition-opacity group-hover/header:opacity-100 has-focus-visible:opacity-100 has-[[data-popup-open]]:opacity-100"
          >
            {action}
          </div>
        ) : null}
      </div>
      <CollapsiblePanel className={TIMELINE_DISCLOSURE_PANEL_CLASS}>{children}</CollapsiblePanel>
    </Collapsible>
  );
}
